<template>
  <manual-page
    title="Gestión de Planes"
    description="Administra los planes de alimentación, desde cotizaciones hasta entregas recurrentes."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'quotesIndex' }"
    action-text="Ver planes"
  >
    <!-- Overview -->
    <h2>¿Qué es un Plan de Alimentación?</h2>
    <p>
      Un plan es una suscripción de alimentación para una mascota. Define el tipo de
      alimento, las porciones, la frecuencia de entrega y el precio. El sistema genera
      órdenes automáticamente según la frecuencia configurada.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Un plan incluye:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Tipo de alimento (dieta asignada)</li>
        <li>Porciones calculadas según peso de la mascota</li>
        <li>Frecuencia de entrega (1-4 semanas)</li>
        <li>Precio por ciclo de entrega</li>
        <li>Método de pago asociado</li>
      </ul>
    </div>

    <!-- States -->
    <h2>Estados de un Plan</h2>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pendiente de Aprobación</h3>
          <p class="text-gray-600">
            Cotización enviada al cliente. Esperando que agregue método de pago,
            dirección, y confirme la activación.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Activo</h3>
          <p class="text-gray-600">
            Plan funcionando normalmente. Se generan órdenes según la frecuencia
            y se cobran automáticamente al cliente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Pausado</h3>
          <p class="text-gray-600">
            Temporalmente suspendido por solicitud del cliente. No se generan
            nuevas órdenes hasta que se reactive.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Suspendido por Pago</h3>
          <p class="text-gray-600">
            Cobros fallidos repetidamente. El plan se suspende automáticamente
            hasta que el cliente resuelva el problema de pago.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cancelado</h3>
          <p class="text-gray-600">
            Plan terminado definitivamente. Las órdenes ya cobradas se entregan
            normalmente, pero no se generan nuevas.
          </p>
        </div>
      </div>
    </div>

    <!-- Detail -->
    <h2>Detalle del Plan</h2>
    <p>Al abrir un plan, verás:</p>
    <ul>
      <li><strong>Información del cliente y mascota:</strong> Con acceso directo a sus perfiles</li>
      <li><strong>Dieta asignada:</strong> Composición detallada del alimento</li>
      <li><strong>Configuración:</strong> Frecuencia, días de entrega, precio</li>
      <li><strong>Ciclo de órdenes:</strong> Próximas órdenes programadas</li>
      <li><strong>Historial:</strong> Órdenes pasadas y cambios realizados</li>
      <li><strong>Método de pago:</strong> Tarjeta asociada al plan</li>
    </ul>

    <!-- Actions -->
    <h2>Acciones de Plan</h2>

    <h3>Modificar Plan</h3>
    <p>Puedes cambiar el tipo de alimento, frecuencia o precio. Los cambios aplican a partir de la próxima orden.</p>

    <h3>Pausar Plan</h3>
    <p>
      Suspende temporalmente las entregas. Útil cuando el cliente va de viaje
      o necesita un descanso temporal.
    </p>

    <h3>Reactivar Plan</h3>
    <p>
      Reanuda un plan pausado. Define la fecha de la próxima entrega y el plan
      vuelve a funcionar normalmente.
    </p>

    <h3>Cancelar Plan</h3>
    <p>
      Termina el plan definitivamente. Las órdenes ya cobradas se entregan,
      pero no se generan nuevas órdenes ni cobros.
    </p>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> La cancelación es irreversible. Si el cliente
        desea volver, deberá solicitar una nueva cotización.
      </p>
    </div>

    <!-- Payment Issues -->
    <h2>Problemas de Pago</h2>
    <p>
      Cuando un cobro falla, el sistema reintenta automáticamente. Después de
      varios intentos fallidos, el plan se suspende. Acciones a tomar:
    </p>
    <ol>
      <li>Revisa el motivo del rechazo (tarjeta vencida, sin fondos, etc.)</li>
      <li>Contacta al cliente para informarle del problema</li>
      <li>El cliente debe actualizar su método de pago</li>
      <li>Una vez actualizado, el sistema reintentará el cobro</li>
    </ol>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Revisa la sección de "Problemas de Pago" regularmente
        para atender estos casos antes de que el plan se suspenda.
      </p>
    </div>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes dudas sobre la gestión de planes:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualPlans",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualQuoteRequests", label: "Solicitudes" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualClients", label: "Clientes" },
        { route: "manualDietManagement", label: "Alimentos" },
      ],
    };
  },
};
</script>
